import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar as CalendarIcon, MapPin, Clock, ArrowRight, AlertCircle } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_LABEL = {
  published: "Publiée",
  accepted: "Acceptée",
  paid: "Payée",
  on_the_way: "En route",
  completed: "Complétée",
  confirmed: "Confirmée",
  paid_out: "Versée",
  cancelled: "Annulée",
};

const STATUS_COLOR = {
  published: "bg-amber-100 text-amber-800",
  accepted: "bg-blue-100 text-blue-800",
  paid: "bg-lime/30 text-navy",
  on_the_way: "bg-lime text-navy",
  completed: "bg-purple-100 text-purple-800",
  confirmed: "bg-lime text-navy",
  paid_out: "bg-slate-200 text-slate-700",
  cancelled: "bg-red-100 text-red-800",
};

function dateKey(d) {
  if (!d) return null;
  const s = String(d);
  return s.length >= 10 ? s.slice(0, 10) : null;
}

function taskDate(t) {
  return dateKey(t.scheduled_date) || dateKey(t.flight_datetime) || dateKey(t.created_at);
}

export default function Calendar() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Date());

  useEffect(() => {
    const url = user.role === "helper" ? "/tasks?mine=helper" : "/tasks?mine=client";
    api.get(url).then((r) => {
      setTasks(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user.role]);

  // Build map of date → tasks
  const tasksByDate = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      const k = taskDate(t);
      if (!k) continue;
      if (!map[k]) map[k] = [];
      map[k].push(t);
    }
    return map;
  }, [tasks]);

  const scheduledDays = useMemo(
    () => Object.keys(tasksByDate).map((k) => new Date(k + "T00:00:00")),
    [tasksByDate]
  );

  const selectedKey = selected ? selected.toISOString().slice(0, 10) : null;
  const dayTasks = selectedKey ? (tasksByDate[selectedKey] || []) : [];

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return tasks
      .filter((t) => {
        const k = taskDate(t);
        return k && k >= today && !["paid_out", "cancelled"].includes(t.status);
      })
      .sort((a, b) => taskDate(a).localeCompare(taskDate(b)))
      .slice(0, 5);
  }, [tasks]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-navy flex items-center gap-2">
            <CalendarIcon size={28} className="text-lime" /> Calendrier
          </h1>
          <p className="text-[#475569] mt-1">
            {user.role === "helper"
              ? "Tes tâches acceptées et à venir."
              : "Tes tâches publiées et planifiées."}
          </p>
        </div>
        {user.role === "client" && (
          <Link to="/tasks/new" className="btn-primary py-2 px-4 text-sm" data-testid="cal-new-task">
            Nouvelle tâche <ArrowRight size={14} />
          </Link>
        )}
      </div>

      {loading ? (
        <div className="card-surface p-10 text-center text-[#475569]">Chargement…</div>
      ) : tasks.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="cal-empty">
          <AlertCircle size={36} className="mx-auto text-[#94A3B8]" />
          <p className="mt-3 text-[#475569]">
            {user.role === "helper"
              ? "Aucune tâche pour le moment. Les tâches que tu acceptes apparaîtront ici."
              : "Tu n'as pas encore publié de tâche."}
          </p>
          {user.role === "client" && (
            <Link to="/tasks/new" className="btn-primary mt-4 inline-flex" data-testid="cal-empty-cta">
              Publier ma 1re tâche <ArrowRight size={14} />
            </Link>
          )}
        </div>
      ) : (
        <div className="grid lg:grid-cols-[auto_1fr] gap-6">
          {/* Calendar */}
          <div className="card-surface p-4 md:p-6 flex justify-center">
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={setSelected}
              modifiers={{ scheduled: scheduledDays }}
              modifiersClassNames={{
                scheduled: "rdp-scheduled",
                selected: "rdp-selected",
              }}
              data-testid="calendar-picker"
              className="text-navy"
            />
          </div>

          {/* Right column */}
          <div className="space-y-4">
            <section className="card-surface p-5" data-testid="cal-day-tasks">
              <h2 className="font-heading font-black text-lg text-navy">
                {selected
                  ? selected.toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
                  : "Sélectionne un jour"}
              </h2>
              <p className="text-xs text-[#475569] mt-0.5">
                {dayTasks.length === 0 ? "Aucune tâche ce jour-là." : `${dayTasks.length} tâche${dayTasks.length > 1 ? "s" : ""}`}
              </p>
              {dayTasks.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {dayTasks.map((t) => (
                    <li key={t.id}>
                      <Link
                        to={`/tasks/${t.id}`}
                        className="block p-3 border-2 border-[#E2E8F0] hover:border-lime rounded-xl transition-colors"
                        data-testid={`cal-day-task-${t.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="font-bold text-navy truncate">{t.title}</div>
                          <span className={`chip ${STATUS_COLOR[t.status] || "bg-surface"}`}>
                            {STATUS_LABEL[t.status] || t.status}
                          </span>
                        </div>
                        <div className="text-xs text-[#475569] mt-1 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1"><MapPin size={12} /> {t.area}</span>
                          {t.flight_datetime && (
                            <span className="flex items-center gap-1"><Clock size={12} />
                              {new Date(t.flight_datetime).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                          <span className="font-bold text-navy">{(t.total || 0).toFixed(2)} $</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {upcoming.length > 0 && (
              <section className="card-surface p-5" data-testid="cal-upcoming">
                <h2 className="font-heading font-black text-lg text-navy">À venir</h2>
                <ul className="mt-3 space-y-2">
                  {upcoming.map((t) => (
                    <li key={t.id}>
                      <Link
                        to={`/tasks/${t.id}`}
                        className="flex items-center gap-3 p-2.5 hover:bg-surface rounded-lg transition-colors"
                        data-testid={`cal-upcoming-${t.id}`}
                      >
                        <div className="h-10 w-10 rounded-lg bg-navy text-lime font-black flex items-center justify-center text-xs shrink-0">
                          {new Date(taskDate(t) + "T00:00:00").toLocaleDateString("fr-CA", { day: "numeric", month: "short" })}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-navy truncate text-sm">{t.title}</div>
                          <div className="text-xs text-[#475569] truncate">{t.area}</div>
                        </div>
                        <span className={`chip text-[10px] ${STATUS_COLOR[t.status] || "bg-surface"}`}>
                          {STATUS_LABEL[t.status] || t.status}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      )}

      <style>{`
        .rdp { --rdp-cell-size: 42px; --rdp-accent-color: #A3E635; --rdp-background-color: #F1F5F9; margin: 0; }
        .rdp-day { font-weight: 600; color: #0A1930; }
        .rdp-day_today { background-color: #F1F5F9; color: #0A1930; }
        .rdp-day:hover:not([disabled]):not(.rdp-selected) { background-color: #F1F5F9; }
        .rdp-scheduled { position: relative; font-weight: 800; color: #0A1930; }
        .rdp-scheduled::after {
          content: ""; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%);
          width: 5px; height: 5px; border-radius: 9999px; background-color: #A3E635;
        }
        .rdp-selected, .rdp-selected:focus, .rdp-selected:hover {
          background-color: #0A1930 !important; color: #A3E635 !important;
        }
        .rdp-selected.rdp-scheduled::after { background-color: #A3E635; }
        .rdp-caption_label { font-family: inherit; font-weight: 800; color: #0A1930; text-transform: capitalize; }
        .rdp-nav_button { color: #0A1930; }
      `}</style>
    </div>
  );
}
